/**
 * Ertragsprognose (Kapitel K).
 *
 * Die Datenquelle ist PVGIS (PV-K01) — der Dienst liefert fuer Standort,
 * Neigung und Azimut die Monatsertraege. Der wird online abgefragt und mit dem
 * Projekt vorgeladen.
 *
 * Dieses Modul ist der Rueckfallweg fuer den Fall ohne Verbindung (PV-B08) und
 * zugleich die Rechnung, die PVGIS nachvollziehbar macht. Aus der
 * Globalstrahlung auf die Waagrechte — ein kleiner regionaler Datensatz, der
 * aufs Geraet passt — wird die Strahlung auf die geneigte Flaeche gerechnet:
 *
 *   1. je Monat den Klarheitsindex bestimmen und daraus den Diffusanteil
 *      (Erbs, Monatskorrelation)
 *   2. Monatssumme auf Stunden verteilen, entlang des Sonnenstands
 *   3. direkt ueber den Einfallswinkel, diffus isotrop, dazu die Reflexion
 *      vom Boden
 *   4. aufsummieren und mit der Anlagenguete multiplizieren
 *
 * Das ist das uebliche Verfahren und kein Ersatz fuer PV*SOL — es soll auch
 * keiner sein. Gefragt ist Geschwindigkeit im Vertrieb.
 */
import { deklination, kosinusEinfallswinkel, sonnenstandZurOrtszeit, tagImJahr, tageslaenge, } from './sonne.js';
import { gradInBogen } from './typen.js';
/**
 * Regionaler Rueckfalldatensatz (PV-B08).
 *
 * Ueber wenige Dutzend Kilometer aendert sich der spezifische Ertrag kaum, fuer
 * ein Angebot reicht die Interpolation nach Neigung und Ausrichtung. Die Werte
 * sind Groessenordnungen fuer das noerdliche Niederoesterreich (Waldviertel,
 * rund 1150 kWh/m² im Jahr auf die Waagrechte) und werden ersetzt, sobald ein
 * PVGIS-Abruf fuer den Standort vorliegt.
 */
export const RUECKFALL_WALDVIERTEL = {
    breiteGrad: 48.45,
    laengeGrad: 15.25,
    quelle: 'Rückfallwerte Waldviertel (Näherung)',
    monate: [26, 44, 82, 122, 156, 163, 168, 145, 100, 60, 28, 21],
};
/** Mittlerer Tag je Monat, an dem gerechnet wird. */
const MITTLERER_TAG = [17, 16, 16, 15, 15, 11, 17, 16, 15, 15, 14, 10];
const TAGE_JE_MONAT = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
/** Solarkonstante in W/m². */
const SOLARKONSTANTE = 1367;
/**
 * Spezifischer Ertrag einer Flaeche.
 *
 * Rueckgabe je kWp, damit die Flaechenleistung nur noch multipliziert werden
 * muss (PV-K01).
 */
export function spezifischerErtrag(eingabe) {
    const guete = eingabe.anlagenguete ?? 0.84;
    const albedo = eingabe.albedo ?? 0.2;
    const abschlag = 1 - (eingabe.verschattungsabschlagProzent ?? 0) / 100;
    const strahlungGeneigt = [];
    let waagrechtGesamt = 0;
    for (let monat = 0; monat < 12; monat++) {
        const tagNummer = tagImJahr(monat + 1, MITTLERER_TAG[monat]);
        const tage = TAGE_JE_MONAT[monat];
        const monatssumme = eingabe.ort.monate[monat] ?? 0;
        waagrechtGesamt += monatssumme;
        // Tagessumme auf der Waagrechten, in kWh/m².
        const tagessumme = monatssumme / tage;
        const geneigterTag = tagesstrahlungGeneigt(eingabe.ort.breiteGrad, tagNummer, tagessumme, eingabe.neigungGrad, eingabe.azimutGrad, albedo);
        strahlungGeneigt.push(geneigterTag * tage);
    }
    const monate = strahlungGeneigt.map((wert) => wert * guete * abschlag);
    const jahr = monate.reduce((summe, wert) => summe + wert, 0);
    const geneigtGesamt = strahlungGeneigt.reduce((summe, wert) => summe + wert, 0);
    return {
        monate,
        jahr,
        strahlungGeneigt,
        neigungsgewinn: waagrechtGesamt > 0 ? geneigtGesamt / waagrechtGesamt : 0,
    };
}
/**
 * Ausserirdische Tagessumme auf die Waagrechte, kWh/m².
 * Der Massstab, an dem sich der Klarheitsindex misst.
 */
export function ausserirdischeTagessumme(breiteGrad, tagNummer) {
    const breite = gradInBogen(breiteGrad);
    const dek = gradInBogen(deklination(tagNummer));
    const wert = Math.max(-1, Math.min(1, -Math.tan(breite) * Math.tan(dek)));
    const sonnenaufgangswinkel = Math.acos(wert);
    const exzentrizitaet = 1 + 0.033 * Math.cos(gradInBogen((360 * tagNummer) / 365));
    const joule = ((24 * 3600) / Math.PI) *
        SOLARKONSTANTE *
        exzentrizitaet *
        (Math.cos(breite) * Math.cos(dek) * Math.sin(sonnenaufgangswinkel) +
            sonnenaufgangswinkel * Math.sin(breite) * Math.sin(dek));
    return joule / 3.6e6;
}
/**
 * Anteil der Diffusstrahlung an der Monatssumme, nach Erbs.
 *
 * Bewusst die Monatskorrelation und nicht die stuendliche: Die Eingangsdaten
 * sind Monatssummen. Wer eine Tages- oder Stundenkorrelation auf einen
 * Monatsmittelwert anwendet, bekommt systematisch zu viel Diffusstrahlung
 * heraus — und damit eine Ertragsrechnung, die kaum noch auf Neigung und
 * Ausrichtung reagiert. Genau daran ist die erste Fassung gescheitert.
 */
export function erbsDiffusanteilMonat(klarheitsindex, sonnenaufgangswinkelGrad) {
    const kt = Math.max(0.05, Math.min(0.8, klarheitsindex));
    const anteil = sonnenaufgangswinkelGrad <= 81.4
        ? 1.391 - 3.56 * kt + 4.189 * kt ** 2 - 2.137 * kt ** 3
        : 1.311 - 3.022 * kt + 3.427 * kt ** 2 - 1.821 * kt ** 3;
    return Math.max(0.1, Math.min(1, anteil));
}
/**
 * Tagessumme auf der geneigten Flaeche, aus der Tagessumme auf der Waagrechten.
 * Gerechnet wird in Stundenschritten ueber den lichten Tag.
 */
function tagesstrahlungGeneigt(breiteGrad, tagNummer, tagessummeWaagrecht, neigungGrad, azimutGrad, albedo) {
    const { aufgang, untergang, stunden } = tageslaenge(breiteGrad, tagNummer);
    if (untergang - aufgang < 0.1)
        return 0;
    // Aufteilung in direkt und diffus einmal je Monat, aus dem Klarheitsindex.
    const ausserirdisch = ausserirdischeTagessumme(breiteGrad, tagNummer);
    if (ausserirdisch <= 0)
        return 0;
    const klarheitsindex = tagessummeWaagrecht / ausserirdisch;
    const diffusanteil = erbsDiffusanteilMonat(klarheitsindex, (stunden / 2) * 15);
    // Erst die Form des Tages bestimmen: wie viel der Tagessumme faellt in welche
    // Stunde? Verteilt wird entlang des Sonnenstands.
    const schritte = [];
    let formsumme = 0;
    for (let stunde = Math.floor(aufgang); stunde <= Math.ceil(untergang); stunde += 1) {
        const mitte = stunde + 0.5;
        if (mitte < aufgang || mitte > untergang)
            continue;
        const stand = sonnenstandZurOrtszeit(breiteGrad, tagNummer, mitte);
        if (stand.hoeheGrad <= 0)
            continue;
        const sinHoehe = Math.sin(gradInBogen(stand.hoeheGrad));
        schritte.push({ stunde: mitte, sinHoehe });
        formsumme += sinHoehe;
    }
    if (formsumme <= 0)
        return 0;
    const neigung = gradInBogen(neigungGrad);
    let geneigt = 0;
    for (const eintrag of schritte) {
        const waagrecht = tagessummeWaagrecht * (eintrag.sinHoehe / formsumme);
        if (waagrecht <= 0)
            continue;
        const diffus = waagrecht * diffusanteil;
        const direktWaagrecht = waagrecht - diffus;
        const stand = sonnenstandZurOrtszeit(breiteGrad, tagNummer, eintrag.stunde);
        const kosEinfall = Math.max(0, kosinusEinfallswinkel(stand, neigungGrad, azimutGrad));
        // Vom waagrechten Strahlungswert auf den Strahl und von dort auf die
        // geneigte Flaeche. Die untere Schranke faengt die flachen Stunden ab, in
        // denen der Faktor sonst ins Unendliche laeuft.
        const direktStrahl = direktWaagrecht / Math.max(0.1, eintrag.sinHoehe);
        geneigt +=
            direktStrahl * kosEinfall +
                diffus * ((1 + Math.cos(neigung)) / 2) +
                waagrecht * albedo * ((1 - Math.cos(neigung)) / 2);
    }
    return geneigt;
}
export function anlagenertrag(flaechen, ort, anlagenguete) {
    const monate = new Array(12).fill(0);
    const jeFlaeche = [];
    for (const flaeche of flaechen) {
        if (flaeche.leistungKwp <= 0)
            continue;
        const ergebnis = spezifischerErtrag({
            ort,
            neigungGrad: flaeche.neigungGrad,
            azimutGrad: flaeche.azimutGrad,
            ...(anlagenguete !== undefined ? { anlagenguete } : {}),
            ...(flaeche.verschattungsabschlagProzent !== undefined
                ? { verschattungsabschlagProzent: flaeche.verschattungsabschlagProzent }
                : {}),
        });
        for (let monat = 0; monat < 12; monat++) {
            monate[monat] = (monate[monat] ?? 0) + (ergebnis.monate[monat] ?? 0) * flaeche.leistungKwp;
        }
        jeFlaeche.push({
            flaecheId: flaeche.flaecheId,
            name: flaeche.name,
            jahr: ergebnis.jahr * flaeche.leistungKwp,
            spezifisch: ergebnis.jahr,
        });
    }
    return { monate, jahr: monate.reduce((summe, wert) => summe + wert, 0), jeFlaeche };
}
//# sourceMappingURL=ertrag.js.map